import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import Icon from 'react-native-vector-icons/Ionicons';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { GlassCard } from '../GlassCard';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { WifiSupportOption, WifiSupportType } from '@/types/residents/wifi';
import { strings } from '@/constants/residents/strings';

interface WifiSupportGlassCardProps {
  options: WifiSupportOption[];
  onPress: (type: WifiSupportType, value: string) => void;
}

const SupportRow: React.FC<{
  option: WifiSupportOption;
  onPress: (type: WifiSupportType, value: string) => void;
  isLast: boolean;
}> = ({ option, onPress, isLast }) => {
  const { colors } = useThemeColors();

  const getIcon = () => {
    switch (option.type) {
      case WifiSupportType.TEXT:
        return {
          component: <RESIDENTS_SVG_COMPONENTS.CHAT_BUBBLE width={18} height={18} />,
          bg: colors.accent.blueBgLight,
        };
      case WifiSupportType.EMAIL:
        return {
          component: <RESIDENTS_SVG_COMPONENTS.EMAIL_BUBBLE width={18} height={18} />,
          bg: colors.accent.purpleLight,
        };
      case WifiSupportType.WEBSITE:
        return {
          component: <Icon name="globe-outline" size={18} color={colors.text.primary} />,
          bg: colors.accent.greenLight,
        };
    }
  };

  const icon = getIcon();

  return (
    <Hoverable
      hover="row"
      style={[styles.supportRow, !isLast && { borderBottomWidth: 1, borderBottomColor: colors.separator }]}
      onPress={() => onPress(option.type, option.value)}
      accessibilityRole="button"
      accessibilityLabel={`${option.title}: ${option.value}`}
    >
      <View style={[styles.iconCircle, { backgroundColor: icon.bg }]}>
        {icon.component}
      </View>
      <View style={styles.supportTextContainer}>
        <Text style={[styles.supportLabel, { color: colors.text.subtle }]}>
          {option.title.toUpperCase()}
        </Text>
        <Text style={[styles.supportValue, { color: colors.text.primary }]}>
          {option.value}
        </Text>
      </View>
    </Hoverable>
  );
};

export const WifiSupportGlassCard: React.FC<WifiSupportGlassCardProps> = ({
  options,
  onPress,
}) => {
  const { colors } = useThemeColors();

  return (
    <GlassCard style={styles.card}>
      <Text style={[styles.title, { color: colors.text.primary }]}>
        {strings.wifi.support.title}
      </Text>
      <Text style={[styles.subtitle, { color: colors.text.muted }]}>
        {strings.wifi.support.subtitle}
      </Text>
      <View style={[styles.divider, { backgroundColor: colors.separator }]} />
      {options.map((option, index) => (
        <SupportRow
          key={option.type}
          option={option}
          onPress={onPress}
          isLast={index === options.length - 1}
        />
      ))}
    </GlassCard>
  );
};

const styles = StyleSheet.create({
  card: {
    marginBottom: 16,
    flex: 1,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.sectionTitle,
    marginBottom: 4,
  },
  subtitle: {
    ...REDESIGN_TYPOGRAPHY.small,
    marginBottom: 16,
  },
  divider: {
    height: 1,
    marginBottom: 4,
  },
  supportRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    gap: 12,
  },
  iconCircle: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  supportTextContainer: {
    flex: 1,
    gap: 2,
  },
  supportLabel: {
    ...REDESIGN_TYPOGRAPHY.label,
  },
  supportValue: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
});
